import { useCallback, useEffect, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import {
  ETemplateStudioSidebarBlock,
  TemplateStudioBlock
} from 'state/template-studio.state.service';

interface FormChange {
  timestamp: number;
  values: any;
  changedFields?: string[];
}

// Map form fields to their containing blocks
const getBlockForField = (fieldPath: string): TemplateStudioBlock | null => {
  // Special cases that don't follow the pattern
  const specialCases: Record<string, TemplateStudioBlock> = {
    templateName: ETemplateStudioSidebarBlock.HEADER,
    bodyBlocksOrder: ETemplateStudioSidebarBlock.HEADER
  };

  // Check special cases first
  if (specialCases[fieldPath]) {
    return specialCases[fieldPath];
  }

  // For most fields, the first part of the path (before the dot) is the block name
  const firstPath = fieldPath.split('.')[0];

  // Map form field names to their corresponding block types
  const fieldToBlockMap: Record<string, TemplateStudioBlock> = {
    header: ETemplateStudioSidebarBlock.HEADER,
    banner: StoreBodyBlock.banners,
    promotion: StoreBodyBlock.specialOffers,
    bundle: StoreBodyBlock.bundles,
    popup: ETemplateStudioSidebarBlock.POPUP,
    rollingOffers: StoreBodyBlock.rollingOffers,
    richText: StoreBodyBlock.richText,
    dailyBonusBadge: StoreBodyBlock.dailyBonusBadge,
    footer: ETemplateStudioSidebarBlock.FOOTER
  };

  return fieldToBlockMap[firstPath] || null;
};

// Helper function to get changed fields between two objects
const getChangedFields = (oldValues: any, newValues: any): string[] => {
  const changedFields: string[] = [];

  const compareObjects = (oldObj: any, newObj: any, path = '') => {
    if (typeof oldObj !== typeof newObj) {
      changedFields.push(path);
      return;
    }

    if (typeof oldObj === 'object' && oldObj !== null && newObj !== null) {
      const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
      allKeys.forEach((key) => {
        const newPath = path ? `${path}.${key}` : key;
        compareObjects(oldObj[key], newObj[key], newPath);
      });
    } else if (oldObj !== newObj) {
      changedFields.push(path);
    }
  };

  compareObjects(oldValues, newValues);
  return changedFields;
};

export const useFormHistory = () => {
  const { watch, reset } = useFormContext();
  const { setOpenSidebarBlock } = useTemplateStudioState();
  const [history, setHistory] = useState<FormChange[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const previousValuesRef = useRef<any>(null);
  const isRestoringRef = useRef(false);
  const isInitializedRef = useRef(false);

  const formValues = watch();

  // Initialize history with current values
  useEffect(() => {
    if (
      formValues &&
      Object.keys(formValues).length > 0 &&
      !isInitializedRef.current
    ) {
      const initialChange: FormChange = {
        timestamp: Date.now(),
        values: JSON.parse(JSON.stringify(formValues))
      };
      setHistory([initialChange]);
      setCurrentIndex(0);
      previousValuesRef.current = JSON.parse(JSON.stringify(formValues));
      isInitializedRef.current = true;
    }
  }, [formValues]);

  // Track changes
  useEffect(() => {
    if (isRestoringRef.current || !formValues || !isInitializedRef.current) {
      return;
    }

    const hasChanged =
      JSON.stringify(formValues) !== JSON.stringify(previousValuesRef.current);

    if (hasChanged) {
      const changedFields = getChangedFields(
        previousValuesRef.current,
        formValues
      );
      const newChange: FormChange = {
        timestamp: Date.now(),
        values: JSON.parse(JSON.stringify(formValues)),
        changedFields
      };

      setHistory((prev) => {
        // Remove any future history when making a new change
        const newHistory = prev.slice(0, currentIndex + 1);
        newHistory.push(newChange);
        return newHistory;
      });

      setCurrentIndex((prev) => prev + 1);
      previousValuesRef.current = JSON.parse(JSON.stringify(formValues));
    }
  }, [formValues, currentIndex]);

  const undo = useCallback(() => {
    if (currentIndex > 0) {
      isRestoringRef.current = true;
      const previousState = history[currentIndex - 1];
      reset(previousState.values, { keepDefaultValues: true });
      setCurrentIndex((prev) => prev - 1);
      previousValuesRef.current = JSON.parse(
        JSON.stringify(previousState.values)
      );

      // Open the block containing the changed fields
      if (
        previousState.changedFields &&
        previousState.changedFields.length > 0
      ) {
        const firstChangedField = previousState.changedFields[0];
        const blockToOpen = getBlockForField(firstChangedField);
        if (blockToOpen) {
          setOpenSidebarBlock(blockToOpen);
        }
      }

      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
    }
  }, [currentIndex, history, reset, setOpenSidebarBlock]);

  const redo = useCallback(() => {
    if (currentIndex < history.length - 1) {
      isRestoringRef.current = true;
      const nextState = history[currentIndex + 1];
      reset(nextState.values, { keepDefaultValues: true });
      setCurrentIndex((prev) => prev + 1);
      previousValuesRef.current = JSON.parse(JSON.stringify(nextState.values));

      // Open the block containing the changed fields
      if (nextState.changedFields && nextState.changedFields.length > 0) {
        const firstChangedField = nextState.changedFields[0];
        const blockToOpen = getBlockForField(firstChangedField);
        if (blockToOpen) {
          setOpenSidebarBlock(blockToOpen);
        }
      }

      setTimeout(() => {
        isRestoringRef.current = false;
      }, 100);
    }
  }, [currentIndex, history, reset, setOpenSidebarBlock]);

  return {
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    undo,
    redo
  };
};
